import { z } from "zod";
import type { Labels } from "./vocabulary.js";

// ---------------------------------------------------------------------------
// 見積の区分 1回目：場所と工種の2つの軸・並べ方（#129、docs/47 §1〜§3、DB 20261003_2010）。
// 行に「場所」「工種」の2つの属性を持たせ、見出しと小計は保存しない。紙（画面・PDF）の並びは layoutLines 1か所で作る。
// manual（手で並べる）は今の紙のまま。並べ方を変えてもデータ（行・金額）は変わらない＝税抜・消費税・税込・率の行の金額は同じ
// ---------------------------------------------------------------------------

/** 紙の並べ方（document_revisions.layout_axis）。既定 manual＝手で並べる（今の紙） */
export const LayoutAxis = z.enum(["manual", "trade", "place", "trade_place", "place_trade"]);
export type LayoutAxis = z.infer<typeof LayoutAxis>;

/** 材工区分（document_lines.work_type）。空でよい */
export const WorkType = z.enum(["material_labor", "material", "labor", "subcontract"]);
export type WorkType = z.infer<typeof WorkType>;
export const WORK_TYPE_LABEL: Record<WorkType, string> = { material_labor: "材工", material: "材料", labor: "手間", subcontract: "外注" };

/** 行の状態（document_lines.line_state）。スマホで作った行は仮、社内で型番・単価を決めたら確定 */
export const LineState = z.enum(["provisional", "final"]);
export type LineState = z.infer<typeof LineState>;

/**
 * 注記の種類（document_lines.note_kind。文章だけの行・別途の行だけ）。scope＝表紙の下の一覧に出す（#129）／plain＝出さない。
 * #307（docs/103 §2.3）で画面の欄はやめた（値は残す・API は変えない）。#308 より前に出した紙の「別途・注記」の枠だけがこの値を見る
 */
export const NoteKind = z.enum(["scope", "plain"]);
export type NoteKind = z.infer<typeof NoteKind>;

/**
 * 分類（工種）・品目の見本のキー（fn_trades_seed・fn_items_seed）。#142 段4 で業種のキー（Industry）に。
 * 前のキー construction（＝builder）・general（＝other）も読む。会社を作ると業種の見本が入る（DB のトリガー）。
 * 設定のボタンは「業種見本追加」1つ＝kind を省く（会社の業種）
 */
export const TradeSeedKind = z.enum(["builder", "electric", "painting", "equipment", "design", "printing", "it", "events", "consulting", "food", "retail", "maintenance", "other", "construction", "general"]);
export type TradeSeedKind = z.infer<typeof TradeSeedKind>;

/**
 * 使う機能（#142 段3、docs/52 §3）で出す並べ方：手動はいつも、{trade}は trades、場所は places、2段は両方のとき。
 * いまの並べ方（current）は OFF の軸でも残す（その見積は今のまま刷る。選び直すと ON の軸と手動だけ）
 */
export function layoutAxesFor(o: { trades: boolean; places: boolean }, current?: LayoutAxis | null): LayoutAxis[] {
  return LayoutAxis.options.filter((a) => a === current || a === "manual" || (a === "trade" ? o.trades : a === "place" ? o.places : o.trades && o.places));
}

/** 画面に出す文（docs/10 §5.1.2）。決まり文はここ1か所。{trade}（工種／分類）は言葉を通す（#142 段3＝画面は t() で） */
export const AXES_MSG = {
  /** 画面の呼び方は「表示順」（#187、10/15 射場さん。前は「並べ方」） */
  layout: "表示順",
  axis: {
    manual: "手動",
    trade: "{trade}",
    place: "場所",
    trade_place: "{trade}→場所",
    place_trade: "場所→{trade}",
  } satisfies Record<LayoutAxis, string>,
  place: "場所",
  trade: "{trade}",
  workType: "材工",
  /** 行の詳細の欄の名前。画面は axesVendorLabel（#142 段6 No.1：現場力は「外注」のまま、ほかは言葉の {vendor}） */
  vendor: "外注",
  other: "その他",
  provisional: "仮",
  finalize: "確定",
  provisionalCount: "仮の行 {n}行",
  /** #308 より前に出した紙（template_snapshot.axes に scopeLine の無い回）の明細表の下の枠の題。画面には出さない */
  scopeHeading: "別途・注記",
  seed: "見本追加",
  breakdownRef: "別紙内訳書通り",
  /** まとめて付ける帯の主のボタン（#139 で「選んだ {n}行に付ける」から。数は帯の頭の「{n}行を選択中」） */
  bulkApply: "一括変更",
  /** API のエラー */
  layoutIssued: "出したあとの見積は、表示順を変えられません",
  paperLayoutAxis: "この書式は表示順「手動」だけです。表示順を「手動」に戻すか、ほかの書式を選んでください",
  tradeNotFound: "その{trade}は見つかりません",
  tradeDuplicate: "同じ名前の{trade}があります（使わないにした{trade}も含みます）",
  tradeTooDeep: "{trade}は2段までです",
  placeNotFound: "その場所は見つかりません",
  placeDuplicate: "同じ名前の場所があります（使わないにした場所も含みます）",
  placeTooDeep: "場所は2段までです",
  linePlaceInvalid: "その場所はこの見積のものではありません",
  lineTradeInvalid: "その{trade}は見つかりません",
  lineVendorInvalid: "{vendor}にできるのは「{vendor}」の取引先だけです",
  inUseArchived: "見積で使っているので、消さずに「使わない」にしました",
  deleted: "消しました",
  seeded: "見本を {n}件入れました",
  seededNone: "見本はもう入っています",
  noLines: "行を選んでください",
} as const;

/** 工種（GET /trades）。parentId があれば2段目。archived＝使わない（一覧と候補に出さない。付いている行の名前には使う） */
export const Trade = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  sortNo: z.number().int(),
  archived: z.boolean(),
});
export type Trade = z.infer<typeof Trade>;
export const TradeList = z.object({ items: z.array(Trade) });
export type TradeList = z.infer<typeof TradeList>;

const NodeName = z.string().trim().min(1, "名前を入れてください").max(50, "名前は50文字までです");

/** POST /trades（管理者） */
export const TradeCreate = z.object({
  name: NodeName,
  code: z.string().trim().max(20).nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
});
export type TradeCreate = z.infer<typeof TradeCreate>;
/** PUT /trades/:id（管理者）。省いた欄は変えない */
export const TradeUpdate = z.object({
  name: NodeName.optional(),
  code: z.string().trim().max(20).nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  archived: z.boolean().optional(),
});
export type TradeUpdate = z.infer<typeof TradeUpdate>;
/** POST /trades/seed（管理者）。kind を省くと会社の業種（業種が無ければ 400 INDUSTRY_REQUIRED） */
export const TradeSeed = z.object({ kind: TradeSeedKind.optional() });
export type TradeSeed = z.infer<typeof TradeSeed>;
/** 並べ替え（POST /trades/reorder・/places/reorder）。同じ親の中の新しい並び（id の順） */
export const NodeReorder = z.object({ ids: z.array(z.string().uuid()).min(1).max(500) });
export type NodeReorder = z.infer<typeof NodeReorder>;

/** 場所（GET /places＝会社の雛形、GET /estimates/:id/places＝その見積の場所）。2段まで（1F ＞ LDK） */
export const EstimatePlace = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  /** null＝会社の雛形 */
  documentId: z.string().uuid().nullable(),
  name: z.string(),
  sortNo: z.number().int(),
  archived: z.boolean(),
});
export type EstimatePlace = z.infer<typeof EstimatePlace>;
export const PlaceList = z.object({ items: z.array(EstimatePlace) });
export type PlaceList = z.infer<typeof PlaceList>;

/** POST /places（会社の雛形。管理者） */
export const PlaceCreate = z.object({ name: NodeName, parentId: z.string().uuid().nullable().optional() });
export type PlaceCreate = z.infer<typeof PlaceCreate>;
/** PUT /places/:id・/estimates/:id/places/:placeId。省いた欄は変えない */
export const PlaceUpdate = z.object({ name: NodeName.optional(), parentId: z.string().uuid().nullable().optional(), archived: z.boolean().optional() });
export type PlaceUpdate = z.infer<typeof PlaceUpdate>;
/**
 * POST /estimates/:id/places（担当者も）：その場で作る（name・parentId）か、会社の雛形から写す（fromPlaceId。2段目なら親も写す）。
 * 同じ名前の場所がもうあれば、それを返す（作らない）
 */
export const EstimatePlaceCreate = z
  .object({ name: NodeName.optional(), parentId: z.string().uuid().nullable().optional(), fromPlaceId: z.string().uuid().optional() })
  .refine((x) => !!x.name !== !!x.fromPlaceId, { message: "名前か、写す場所を1つだけ指定します" });
export type EstimatePlaceCreate = z.infer<typeof EstimatePlaceCreate>;

/** PUT /estimates/:id/layout（下書きだけ。出した回は 409 LAYOUT_ISSUED） */
/**
 * PUT /estimates/:id/layout・/invoices/:id/layout。version＝画面が読んだときの updatedAt（データが消えうる不具合の直し、2026-10-13）：
 * 違えば 409（ほかで変更があった。並べ方も版を進めるので、確かめずに版を受け取ると、あとの保存がほかの直しを消しうる）。省けば見ない
 */
export const LayoutUpdate = z.object({ axis: LayoutAxis, version: z.string().max(40).optional() });
export type LayoutUpdate = z.infer<typeof LayoutUpdate>;

/** POST /estimates/:id/lines/attrs：選んだ行にまとめて付ける。省いた欄は変えない・null で外す */
export const LineAttrsUpdate = z.object({
  lineIds: z.array(z.string().uuid()).min(1, AXES_MSG.noLines).max(500),
  placeId: z.string().uuid().nullable().optional(),
  tradeId: z.string().uuid().nullable().optional(),
  workType: WorkType.nullable().optional(),
  vendorPartnerId: z.string().uuid().nullable().optional(),
});
export type LineAttrsUpdate = z.infer<typeof LineAttrsUpdate>;
/** POST /estimates/:id/lines/finalize：仮 → 確定（仮のメモは残す） */
export const LinesFinalize = z.object({ lineIds: z.array(z.string().uuid()).min(1, AXES_MSG.noLines).max(500) });
export type LinesFinalize = z.infer<typeof LinesFinalize>;

/** 並べ方で使う木の1つ（工種・場所）。名前は出した回なら固めた名前 */
export const AxisNode = z.object({ id: z.string().uuid(), parentId: z.string().uuid().nullable(), name: z.string(), sortNo: z.number().int() });
export type AxisNode = z.infer<typeof AxisNode>;

/**
 * 見積（請求書・領収書）の並べ方の情報（Estimate.axes）。出した回は template_snapshot.axes に固めた値（名前・設定ごと）、
 * 下書きはいまの工種マスタ・その見積の場所・会社の設定
 */
export const AxesInfo = z.object({
  axis: LayoutAxis,
  /** 工種（使わないにしたものも含む＝付いている行の名前に使う） */
  trades: z.array(AxisNode),
  /** その帳票の場所 */
  places: z.array(AxisNode),
  /** 印刷で同じ名称を「〃」に（会社の設定。出した回は固めた値） */
  ditto: z.boolean(),
  /**
   * 会社の設定 tenants.print_scope_notes（出した回は固めた値）。#308（docs/103 §3）から「別途の一覧を備考に印字」＝備考の先頭に「別途：…」の1行。
   * scopeLine の無い回（#308 より前に出した回）は前の意味＝明細表の下の「別途・注記」の枠（note_kind=scope の行）
   */
  scopeNotes: z.boolean(),
  /** #308 の形か（下書きはいつも true・出すときに固まる）。無い・false＝前の「別途・注記」の枠（出した紙を変えない） */
  scopeLine: z.boolean().optional(),
  /** 出した回の固めた値か */
  frozen: z.boolean(),
});
export type AxesInfo = z.infer<typeof AxesInfo>;

/** 出すときに template_snapshot に固める形（PaperSnapshot.axes） */
export const AxesSnapshot = AxesInfo.omit({ frozen: true });
export type AxesSnapshot = z.infer<typeof AxesSnapshot>;

// ---------------------------------------------------------------------------
// 並べ方の計算（画面の紙と API の PDF が同じ関数を使う）。金額は計算しない（DB の値を足すだけ）
// ---------------------------------------------------------------------------

/** 金額を持つ行（DB の fn_row_has_amount と同じ）。グループの小計はこの行の金額の和＝全部のグループの和は税抜合計 */
export const AMOUNT_ROLES: ReadonlySet<string> = new Set(["detail", "overhead_rate", "discount"]);

/** layoutLines に渡す行（EstimateLine を そのまま渡せる） */
export type AxisLine = {
  id: string;
  role: string;
  name: string;
  amount: number;
  sheetId: string | null;
  placeId?: string | null | undefined;
  tradeId?: string | null | undefined;
  noteKind?: NoteKind | null | undefined;
};
/** 内訳の紙（手で並べる＝今の紙のとき使う）。subtotal は DB が計算した値 */
export type AxisSheet = { id: string; type: "cover" | "breakdown"; name: string; note?: string | undefined; subtotal: number };

/** 出力の1行 */
export type LayoutRow<L extends AxisLine> =
  /** 行そのもの（level：0＝表紙、1＝紙の中、2＝小見出しの中） */
  | { kind: "line"; line: L; level: 0 | 1 | 2 }
  /** 作った見出し（level 1＝紙・グループの名前、2＝小見出し） */
  | { kind: "heading"; key: string; name: string; note: string; level: 1 | 2 }
  /** 作った小計「○○ 計」 */
  | { kind: "subtotal"; key: string; name: string; amount: number; level: 1 | 2 }
  /** 表紙の「1式（別紙内訳書通り）」の行。page＝何番目の内訳の紙か（0から） */
  | { kind: "summary"; key: string; name: string; note: string; amount: number; hasAmount: boolean; page: number };

/** 内訳の紙1枚（手で並べる＝今の内訳の紙、軸のとき＝1段目のグループ） */
export type LayoutPage<L extends AxisLine> = { key: string; name: string; note: string; subtotal: number; hasAmount: boolean; rows: LayoutRow<L>[] };

export type LayoutResult<L extends AxisLine> = {
  axis: LayoutAxis;
  /** 表紙＋内訳の紙のときの表紙：内訳の紙ごとの「1式」の行 → 表紙の行（軸のときは表紙の行は無い） */
  cover: LayoutRow<L>[];
  /** 内訳の紙 */
  pages: LayoutPage<L>[];
  /** 1枚に続けたとき（画面・続けて印字）：見出し → 行 → 「○○ 計」、最後に表紙の行 */
  flat: LayoutRow<L>[];
  /** 範囲の注記（note_kind=scope）。元の並び順 */
  scope: L[];
  /** 場所・工種の付いていない ふつうの行の数（社内の知らせ「場所なし n行」） */
  unassigned: { place: number; trade: number };
};

const NONE = "none";

/** 木の並び（1段目の sortNo → 名前、その下に2段目）と、見出しの名前（「2F ＞ 子供部屋」） */
function treeIndex(nodes: AxisNode[]): Map<string, { order: number; label: string }> {
  const byName = (a: AxisNode, b: AxisNode) => a.sortNo - b.sortNo || a.name.localeCompare(b.name, "ja");
  const tops = nodes.filter((x) => !x.parentId || !nodes.some((p) => p.id === x.parentId)).sort(byName);
  const out = new Map<string, { order: number; label: string }>();
  let i = 0;
  for (const t of tops) {
    out.set(t.id, { order: i++, label: t.name });
    for (const c of nodes.filter((x) => x.parentId === t.id).sort(byName)) out.set(c.id, { order: i++, label: `${t.name} ＞ ${c.name}` });
  }
  return out;
}

const sumOf = (ls: AxisLine[]): number => ls.reduce((t, l) => t + (AMOUNT_ROLES.has(l.role) ? l.amount : 0), 0);
const hasAmountOf = (ls: AxisLine[]): boolean => ls.some((l) => AMOUNT_ROLES.has(l.role));

/**
 * 並べ方の計算（#129、docs/47 §3）。画面（PC の紙）と API（PDF）が同じ関数を使う。
 *   ・manual：今の並びをそのまま（表紙＝内訳の紙の「1式」→ 表紙の行、内訳の紙＝今の内訳）
 *   ・trade／place：1段目のグループ（工種・場所）ごとに内訳の紙。中はもう一方の軸の順 → 元の並び
 *   ・trade_place／place_trade：グループの紙の中に、もう一方の軸の小見出し（小見出しが2つ以上なら「○○ 計」も）
 *   ・属性の無い行は「その他」（いちばん後ろ）。手で作った見出し・小計の行は出さない（データは消さない）
 *   ・グループの小計は 金額を持つ行（ふつう・率・値引き）の金額の和。別途・文章は 0。全部のグループの和＝税抜合計
 *   ・率の行の基礎は docs/17 のまま（DB が手で並べた位置で計算した金額をそのまま使う）。作った見出し・小計は基礎にならない
 */
export function layoutLines<L extends AxisLine>(lines: L[], sheets: AxisSheet[], axis: LayoutAxis, ctx: { trades: AxisNode[]; places: AxisNode[] }): LayoutResult<L> {
  const scope = lines.filter((l) => l.noteKind === "scope");
  const details = lines.filter((l) => l.role === "detail");
  const unassigned = { place: details.filter((l) => !l.placeId).length, trade: details.filter((l) => !l.tradeId).length };

  if (axis === "manual") {
    const cover = sheets.find((x) => x.type === "cover");
    const breakdowns = sheets.filter((x) => x.type === "breakdown");
    const coverLines = cover ? lines.filter((l) => l.sheetId === cover.id || l.sheetId == null) : breakdowns.length ? lines.filter((l) => l.sheetId == null) : lines;
    const pages: LayoutPage<L>[] = breakdowns.map((s) => {
      const ls = lines.filter((l) => l.sheetId === s.id);
      return { key: `s:${s.id}`, name: s.name, note: s.note ?? "", subtotal: s.subtotal, hasAmount: hasAmountOf(ls), rows: ls.map((line) => ({ kind: "line" as const, line, level: 1 as const })) };
    });
    const coverRows: LayoutRow<L>[] = coverLines.map((line) => ({ kind: "line", line, level: 0 }));
    return {
      axis,
      cover: [...pages.map((p, i): LayoutRow<L> => ({ kind: "summary", key: p.key, name: p.name, note: p.note, amount: p.subtotal, hasAmount: p.hasAmount, page: i })), ...coverRows],
      pages,
      flat: [
        ...pages.flatMap((p): LayoutRow<L>[] => [
          { kind: "heading", key: p.key, name: p.name, note: p.note, level: 1 },
          ...p.rows,
          { kind: "subtotal", key: p.key, name: p.name, amount: p.subtotal, level: 1 },
        ]),
        ...coverRows,
      ],
      scope,
      unassigned,
    };
  }

  const primaryIsTrade = axis === "trade" || axis === "trade_place";
  const twoLevel = axis === "trade_place" || axis === "place_trade";
  const tIdx = treeIndex(ctx.trades);
  const pIdx = treeIndex(ctx.places);
  const keyOf = (l: L, trade: boolean): string => {
    const id = trade ? l.tradeId : l.placeId;
    return id && (trade ? tIdx : pIdx).has(id) ? id : NONE;
  };
  const orderOf = (key: string, trade: boolean): number => (key === NONE ? Number.MAX_SAFE_INTEGER : (trade ? tIdx : pIdx).get(key)!.order);
  const labelOf = (key: string, trade: boolean): string => (key === NONE ? AXES_MSG.other : (trade ? tIdx : pIdx).get(key)!.label);
  const prefix = (trade: boolean) => (trade ? "t" : "p");

  // 手で作った見出し・小計は出さない。元の並び（index）は保つ
  const body = lines.map((line, index) => ({ line, index })).filter((x) => x.line.role !== "group" && x.line.role !== "subtotal");
  const primaryKeys = [...new Set(body.map((x) => keyOf(x.line, primaryIsTrade)))].sort((a, b) => orderOf(a, primaryIsTrade) - orderOf(b, primaryIsTrade));

  const pages: LayoutPage<L>[] = primaryKeys.map((pk) => {
    const inGroup = body
      .filter((x) => keyOf(x.line, primaryIsTrade) === pk)
      .sort((a, b) => orderOf(keyOf(a.line, !primaryIsTrade), !primaryIsTrade) - orderOf(keyOf(b.line, !primaryIsTrade), !primaryIsTrade) || a.index - b.index);
    const ls = inGroup.map((x) => x.line);
    const key = `${prefix(primaryIsTrade)}:${pk}`;
    let rows: LayoutRow<L>[];
    if (!twoLevel) {
      rows = ls.map((line) => ({ kind: "line", line, level: 1 }));
    } else {
      const subKeys = [...new Set(ls.map((l) => keyOf(l, !primaryIsTrade)))];
      rows = subKeys.flatMap((sk): LayoutRow<L>[] => {
        const sub = ls.filter((l) => keyOf(l, !primaryIsTrade) === sk);
        const skey = `${key}/${prefix(!primaryIsTrade)}:${sk}`;
        const name = labelOf(sk, !primaryIsTrade);
        return [
          { kind: "heading", key: skey, name, note: "", level: 2 },
          ...sub.map((line): LayoutRow<L> => ({ kind: "line", line, level: 2 })),
          ...(subKeys.length > 1 ? [{ kind: "subtotal" as const, key: skey, name, amount: sumOf(sub), level: 2 as const }] : []),
        ];
      });
    }
    return { key, name: labelOf(pk, primaryIsTrade), note: "", subtotal: sumOf(ls), hasAmount: hasAmountOf(ls), rows };
  });

  return {
    axis,
    cover: pages.map((p, i): LayoutRow<L> => ({ kind: "summary", key: p.key, name: p.name, note: p.note, amount: p.subtotal, hasAmount: p.hasAmount, page: i })),
    pages,
    flat: pages.flatMap((p): LayoutRow<L>[] => [
      { kind: "heading", key: p.key, name: p.name, note: p.note, level: 1 },
      ...p.rows,
      { kind: "subtotal", key: p.key, name: p.name, amount: p.subtotal, level: 1 },
    ]),
    scope,
    unassigned,
  };
}

/**
 * 「〃」（#129、docs/47 決めごと10）：印刷のとき、前の行と名称が同じ ふつうの行の名称を「〃」に。
 * 見出し（作った見出し・手で作った見出し）・小計・紙の区切り・文章の行をまたいだら比べ直す。データ（name）は変えない。
 * 返すのは「〃」にする行の id → "〃"
 */
export function dittoNames<L extends AxisLine>(rows: LayoutRow<L>[]): Map<string, string> {
  const out = new Map<string, string>();
  let prev: string | null = null;
  for (const r of rows) {
    if (r.kind !== "line") {
      prev = null;
      continue;
    }
    const l = r.line;
    if (l.role !== "detail") {
      prev = null;
      continue;
    }
    if (l.name !== "" && l.name === prev) out.set(l.id, "〃");
    prev = l.name;
  }
  return out;
}

/**
 * 行の詳細の「外注」の欄の名前（#142 段6 No.1）。言葉の {vendor} が既定の「外注先」（現場力）なら今までどおり短い「外注」、
 * ほか（営業力の「仕入先」や会社の言い換え）はその言葉をそのまま使う
 */
export function axesVendorLabel(labels: Pick<Labels, "vendor">): string {
  return labels.vendor === "外注先" ? AXES_MSG.vendor : labels.vendor;
}

/** 備考の先頭の1行の頭（#308） */
export const SCOPE_LINE_HEAD = "別途：";
/**
 * 備考の先頭の「別途：AAAA、BBBB」（#308、docs/103 §3）。区分「別途」の行の名称を並びのまま（名称が空の行・金額欄の文字に「別途」を含まない行
 * ＝「サービス」「含む」「込」「無料」は入れない）。説明の行は入れない。渡すのは紙に出す行（使う内訳の行＝printableOf のあと）
 */
export function scopeLineOf(lines: ReadonlyArray<{ role: string; name: string; amountLabel?: string | null | undefined }>): string {
  const names = lines.filter((l) => l.role === "excluded" && (l.amountLabel || "別途").includes("別途")).map((l) => l.name.trim()).filter(Boolean);
  return names.length ? SCOPE_LINE_HEAD + names.join("、") : "";
}
/** 紙に「別途：…」の1行を出すか（#308 の形で、設定がオンのとき）。出すならその文、出さなければ空 */
export function scopeLineFor(e: { axes?: { scopeNotes: boolean; scopeLine?: boolean | undefined } | null | undefined; lines: ReadonlyArray<{ role: string; name: string; amountLabel?: string | null | undefined }> }): string {
  return e.axes?.scopeLine && e.axes.scopeNotes ? scopeLineOf(e.lines) : "";
}
